import { useState, useCallback } from 'react';
import type { ReadingCollection } from '../types/collections';

// Mock data for collections - in a real app this would come from API
const mockCollections: ReadingCollection[] = [
  {
    id: '1',
    name: 'Love & Relationships',
    description: 'Readings about love, relationships, and emotional connections',
    color: '#ef4444',
    readingIds: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Career & Finance',
    description: 'Professional guidance and financial insights',
    color: '#10b981',
    readingIds: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Spiritual Growth',
    description: 'Personal development and spiritual journey',
    color: '#8b5cf6',
    readingIds: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

export function useReadingCollections() {
  const [collections, setCollections] = useState<ReadingCollection[]>(mockCollections);

  const createCollection = useCallback((collection: Omit<ReadingCollection, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newCollection: ReadingCollection = {
      ...collection,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setCollections(prev => [...prev, newCollection]);
    return newCollection;
  }, []);

  const updateCollection = useCallback((id: string, updates: Partial<ReadingCollection>) => {
    setCollections(prev => prev.map(collection => 
      collection.id === id 
        ? { ...collection, ...updates, updatedAt: new Date().toISOString() }
        : collection
    ));
  }, []);

  const deleteCollection = useCallback((id: string) => {
    setCollections(prev => prev.filter(collection => collection.id !== id));
  }, []);

  const addReadingToCollection = useCallback((collectionId: string, readingId: string) => {
    setCollections(prev => prev.map(collection => 
      collection.id === collectionId
        ? { 
            ...collection, 
            readingIds: [...collection.readingIds, readingId],
            updatedAt: new Date().toISOString()
          }
        : collection
    ));
  }, []);

  const removeReadingFromCollection = useCallback((collectionId: string, readingId: string) => {
    setCollections(prev => prev.map(collection => 
      collection.id === collectionId
        ? { 
            ...collection, 
            readingIds: collection.readingIds.filter(id => id !== readingId),
            updatedAt: new Date().toISOString()
          }
        : collection
    ));
  }, []);

  const getCollectionsForReading = useCallback((readingId: string) => {
    return collections.filter(collection => collection.readingIds.includes(readingId));
  }, [collections]);

  return {
    collections,
    createCollection,
    updateCollection,
    deleteCollection,
    addReadingToCollection,
    removeReadingFromCollection,
    getCollectionsForReading
  };
}